/* eslint-disable  @typescript-eslint/no-explicit-any */
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button, InputText, Loading } from '../../components';
import styles from './styles.module.css';
import validation from '../../utils/validation';
import { useConfig } from '../../services/ConfigProvider';
import { forgotPassword } from '../../services/Login';
import useTranslation from '../../utils/translation';
import emailMask from '../../utils/helper/email-mask';
import {
  pushPageViewDataLayer,
  pushToDataLayer,
  sendErrorToGa,
} from '../../services/googleTagManager';
import { ResetEmailResponse } from './types';

interface Props {
  closePopupFunction?: () => void;
}

const ForgotPass = (props: Props) => {
  const { closePopupFunction } = props;

  const [forgotPass, setForgotPass] = useState('');
  const [error, setError] = useState(false);
  const [valid, setValid] = useState(false);
  const [textErrorEmail, setTextErrorEmail] = useState('');
  const [resetEmailResponse, setResetEmailResponse] =
    useState<ResetEmailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResetEmailModal, setShowResetEmailModal] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);

  const config = useConfig();
  const COUNTRY = config?.REACT_APP_COUNTRY || 'BR';
  const API_URL = config?.REACT_APP_LOGIN_ADAPTER_URL || '';

  useEffect(() => {
    pushPageViewDataLayer('login - esqueceu a senha', 'visualizado');
  }, []);

  const translate = useTranslation(COUNTRY);

  const validateLogin = useCallback(
    (text: string) => {
      if (validation.emailValidation(text)) return true;
      const numbers = onlyNumbers(text);
      if (COUNTRY === 'BR' && validation.cpfValidation(numbers)) return true;
      return false;
    },
    [COUNTRY],
  );

  const onlyNumbers = (text: string) => {
    return text.replace(/[^0-9]/g, '');
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const identify = e.target.value;

    setValid(validateLogin(identify));
    setForgotPass(e.target.value);
  };

  const handleValidateForgot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;

    if (validateLogin(text)) {
      setValid(true);
      setError(false);
      return;
    }
    setTextErrorEmail(translate['forgot-password-form-validate']);
    setValid(false);
    setError(true);
    sendErrorToGa('login', 'esqueceu a senha', 'falha ao preencher e-mail');
    return;
  };

  const handleSubmitForm = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    setLoading(true);
    try {
      const username = !forgotPass.includes('@')
        ? onlyNumbers(forgotPass)
        : forgotPass;

      setResetEmailResponse(await forgotPassword(API_URL, username));
      setLoading(false);
      setError(false);

      pushToDataLayer(
        'login',
        'esqueceu a senha',
        emailMask(forgotPass),
        'e-mail enviado com sucesso',
      );
      setShowResetEmailModal(true);
    } catch (e) {
      setLoading(false);
      setError(true);
      setTextErrorEmail(translate['forgot-password-form-validate']);
      sendErrorToGa('login', 'esqueceu a senha', 'erro ao enviar e-mail');
      setResetEmailResponse(null);
    }
  };

  const handleClosePopup = () => {
    if (closePopupFunction != undefined) {
      closePopupFunction();
    }
    pushToDataLayer('login', 'esqueceu a senha', '', 'Popup closed');
  };

  return (
    <>
      <Loading visibled={loading} />
      {!showResetEmailModal && !showQRCodeModal && (
        <div className={styles.forgotpass}>
          <h1>{translate['forgot-password-my-password']}</h1>
          <p>{translate['forgot-password-help-change']}</p>
          <form className={styles.form}>
            <InputText
              name="forgot"
              placeholder={translate['forgot-password-form-email']}
              type="text"
              error={error}
              textError={textErrorEmail}
              required={true}
              value={forgotPass}
              setValue={setForgotPass}
              onBlur={handleValidateForgot}
              onChange={handleTextChange}
            />
            <Button
              type="submit"
              text={translate['forgot-password-redefine']}
              disabled={!valid}
              onClick={handleSubmitForm}
            />
          </form>
        </div>
      )}
      {showResetEmailModal && !showQRCodeModal && resetEmailResponse && (
        <div className={styles.resetEmail}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h6>{translate['forgot-password-help-change']}</h6>
            <p>
              {resetEmailResponse.title ||
                translate['forgot-password-change-email-title']}
            </p>
            <strong
              dangerouslySetInnerHTML={{
                __html:
                  resetEmailResponse.message ||
                  translate['forgot-password-send-email'].replace(
                    'email-sent',
                    forgotPass,
                  ),
              }}
            />
          </div>

          {COUNTRY === 'CO' && (
            <footer>
              <div className={styles.resetEmailButtonWrapperFull}>
                <Button
                  type="submit"
                  text={translate['forgot-password-go-back-to-login']}
                  onClick={handleClosePopup}
                />
              </div>
            </footer>
          )}

          {COUNTRY === 'BR' && (
            <footer>
              <div className={styles.resetEmailButtonWrapper}>
                <Button
                  type="submit"
                  text={translate['forgot-password-go-back-to-login']}
                  onClick={handleClosePopup}
                />
              </div>
              <div className={styles.resetEmailButtonWrapper}>
                <Button
                  type="button"
                  text={translate['forgot-password-change-email']}
                  onClick={() => {
                    setShowResetEmailModal(false);
                    setShowQRCodeModal(true);
                  }}
                />
              </div>
            </footer>
          )}
        </div>
      )}
      {showQRCodeModal && !showResetEmailModal && resetEmailResponse && (
        <div className={styles.forgotpass}>
          <p
            className={styles.qrCodeTitle}
            dangerouslySetInnerHTML={{
              __html: translate['forgot-password-change-email-title'],
            }}
          />
          <p className={styles.qrCodeSubtitle}>
            {translate['forgot-password-qr-code-title']}
          </p>
          <div id="qrCodeDiv" className={styles.qrCode}>
            <QRCodeCanvas
              value={resetEmailResponse.qrcode_url}
              size={window.innerWidth > 980 ? 128 : 200}
            />
          </div>
          <div className={styles.qrCodeActionButton}>
            <Button
              type="submit"
              text={translate['forgot-password-go-back-to-login']}
              onClick={handleClosePopup}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ForgotPass;
